import { Component, OnInit, HostBinding, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { BehaviorSubject, Subscription, combineLatest } from "rxjs";
import { TelephoneNumberAddress } from "@onsip/common/services/api/resources/telephoneNumberAddress/telephone-number-address";
import { TelephoneNumberAddressExtended } from "../interfaces/external-phone-number-extended";
import { TelephoneNumberAddressService } from "@onsip/common/services/api/resources/telephoneNumberAddress/telephone-number-address.service";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import { AdminTableAbstractComponent } from "@onsip/web/features/abstractClass/admin-table-abstract.component";
import { filter, map } from "rxjs/operators";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { TelephoneNumberAddressIdentityService } from "@onsip/common/services/api/resources/telephoneNumberAddress/telephone-number-address-identity.service";
import { TelephoneNumberAddressIdentity } from "@onsip/common/services/api/resources/telephoneNumberAddress/telephone-number-address-identity";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import { UserAliasService } from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { FormatE164Pipe } from "@onsip/web/features/shared/pipes/format-e164.pipe";

export interface TelephoneNumberAddressWithCallerId extends TelephoneNumberAddressExtended {
  callerIdName: string;
  callerIdNumber: string;
  reportCallActivity: string;
}

@Component({
  selector: "onsip-external-phone-number",
  templateUrl: "./external-phone-number-page.component.html",
  styleUrls: ["./external-phone-number-page.component.scss"],
  providers: [FormatE164Pipe],
  standalone: false
})
export class ExternalPhoneNumberPageComponent
  extends AdminTableAbstractComponent<TelephoneNumberAddressWithCallerId>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;

  hasExternalPhoneNumbers = new BehaviorSubject<boolean>(true);
  isDataObtained$ = new BehaviorSubject(false);
  isEditMode$ = new BehaviorSubject<boolean>(false);
  E2eLocators = E2eLocators;

  protected displayedColumnsRecord = {
    XSmall: ["name", "actions"],
    Small: ["name", "externalPhoneNumber", "actions"],
    Medium: ["name", "externalPhoneNumber", "outboundCallerId", "actions"],
    Large: ["name", "externalPhoneNumber", "outboundCallerId", "actions"],
    XLarge: ["name", "externalPhoneNumber", "outboundCallerId", "actions"]
  };

  protected unsubscriber = new Subscription();

  constructor(
    protected translate: TranslateService,
    protected breakpointObserver: BreakpointObserver,
    protected numberAddressService: TelephoneNumberAddressService,
    protected addressIdentityService: TelephoneNumberAddressIdentityService,
    protected userAliasService: UserAliasService,
    protected dialog: MatDialog,
    protected snackbar: SnackbarService,
    protected userService: UserService,
    protected cdr: ChangeDetectorRef,
    private formatE164: FormatE164Pipe
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setDataFilter();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  trackByAddressId(index: number, item: TelephoneNumberAddressWithCallerId): string {
    return item.addressId;
  }

  protected getItemName({ name }: TelephoneNumberAddressWithCallerId): string {
    return name;
  }

  protected getItemValueByColumn(
    item: TelephoneNumberAddressWithCallerId,
    columnName: string
  ): string | number {
    switch (columnName) {
      case "name":
        return item.name.trim().toLowerCase();
      case "externalPhoneNumber":
        return item.username;
      case "outboundCallerId":
        return item.callerIdName;
      default:
        return parseInt(item.addressId);
    }
  }

  protected initDataSource(): void {
    this.numberAddressService.telephoneNumberAddressBrowse();
    this.addressIdentityService.telephoneNumberAddressIdentityBrowse();
    this.userAliasService.userAliasBrowse();
    this.unsubscriber.add(
      combineLatest({
        telephoneNumberAddresses: this.numberAddressService.orgState,
        addressIdentities: this.addressIdentityService.orgState,
        users: this.userService.state.pipe(
          filter(({ loading }) => !loading),
          map(({ state }) => Object.values(state))
        ),
        userAliases: this.userAliasService.orgState
      })
        .pipe(
          map(({ telephoneNumberAddresses, addressIdentities, users, userAliases }) => {
            const telNumAddressWithIds = telephoneNumberAddresses.map(
              (telNumAdd: TelephoneNumberAddress) => ({
                ...telNumAdd,
                callerIdName:
                  addressIdentities.find(
                    (addId: TelephoneNumberAddressIdentity) => addId.username === telNumAdd.username
                  )?.callerIdName || telNumAdd.name,
                callerIdNumber:
                  addressIdentities.find(
                    (addId: TelephoneNumberAddressIdentity) => addId.username === telNumAdd.username
                  )?.callerIdNumber || telNumAdd.username,
                reportCallActivity:
                  users.find(user => user.userId === telNumAdd.userId)?.contact.name ||
                  "Authenticated user",
                extensions: userAliases
                  .filter(extension => extension.addressId === telNumAdd.addressId)
                  .map(filteredExtension => filteredExtension.aliasUsername)
              })
            );
            return telNumAddressWithIds;
          })
        )
        .subscribe(telNumAddressWithIds => {
          this.dataSource.data = telNumAddressWithIds;
          if (this.dataSource.data.length) {
            this.hasExternalPhoneNumbers.next(true);
          } else {
            this.hasExternalPhoneNumbers.next(false);
          }
          this.isDataObtained$.next(true);
        })
    );
  }

  protected performDeleteRequest(address: TelephoneNumberAddressWithCallerId): void {
    this.numberAddressService
      .telephoneNumberAddressDelete(address.username + "@" + address.domain)
      .then(() => {
        if (this.dataSource.data.length === 0) {
          this.hasExternalPhoneNumbers.next(false);
        } else {
          this.hasExternalPhoneNumbers.next(true);
        }
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_DELETED", {
            value: "External phone number"
          }),
          "success"
        );
        this.cdr.markForCheck();
      });
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // name
      const name = data.name;
      // phone number
      const number = this.formatE164.transform(data.username);
      // callerIdName
      const callerIdName = data.callerIdName;

      return (
        name.toLowerCase().includes(filterValue) ||
        number.toLowerCase().includes(filterValue) ||
        callerIdName.toLowerCase().includes(filterValue)
      );
    };
  }
}
